import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProIcon } from '../components/ProIcon';
import { COMPANY_INFO } from '../config/company';
import { trackContactClick, trackEvent } from '../lib/analytics';

// Service data with SEO-optimized content
const services = [
  {
    id: 'rental-matching',
    icon: 'magnifying-glass',
    title: 'Rental Matching',
    headline: 'Get Matched to the Right Rental',
    description: "Tell us the dates, group, budget, and must-haves. We shortlist the right homes. No algorithm. No wasted time.",
    ctaLabel: 'Find Your Rental',
    ctaTo: '/destinations',
    ctaTrack: 'rental_matching',
  },
  {
    id: 'pre-arrival',
    icon: 'calendar-check',
    title: 'Pre-Arrival Planning',
    headline: 'Arrive to Everything Ready',
    description: "Transfers, groceries, baby gear, chef bookings, activity planning. Sorted before you land.",
  },
  {
    id: 'inspection',
    icon: 'clipboard-check',
    title: 'Personal Inspection',
    headline: 'Every Rental Personally Checked',
    description: "We inspect every home before it goes live. If it does not hold up in person, we do not list it.",
  },
  {
    id: 'housekeeping',
    icon: 'broom',
    title: 'Housekeeping',
    headline: 'Keep Your Holiday Comfortable',
    description: 'Mid-stay cleaning on request. Fresh linen, tidy spaces, less hassle.',
  },
  {
    id: 'family-essentials',
    icon: 'baby',
    title: 'Family Essentials',
    headline: 'Everything Your Family Needs',
    description: "Cots, high chairs, stair gates, and pool safety gear. Ready before arrival.",
  },
  {
    id: 'owner-partnerships',
    icon: 'key',
    title: 'Owner Partnerships',
    headline: 'Own a Property in Marbella?',
    description: 'Selective partnerships for owners who want stronger presentation, better guests, and local oversight.',
    ctaLabel: 'Partner With Us',
    ctaTo: '/owners',
    ctaTrack: 'owner_partnerships',
  },
];

// Trust signals data
const trustSignals = [
  { icon: 'headset', value: '2hr', label: 'Avg. Response Time' },
  { icon: 'star', value: '4.9/5', label: 'Guest Rating' },
  { icon: 'clock', value: '24/7', label: 'Support Available' },
  { icon: 'shield-alt', value: '100%', label: 'Inspected Rentals' },
];

export const Services = () => {
  return (
    <main className="min-h-screen bg-white pt-24" role="main" aria-label="Guest Services">
      {/* Hero Section - SEO Optimized */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="ui-eyebrow-brand mb-4">Guest Services</p>
          <h1 className="ui-page-title mb-6 max-w-4xl mx-auto">
            A Real Team Behind Every Stay.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Most rentals hand over keys and vanish. We do not. You deal with a Marbella team that knows the home, knows the area, and answers fast.
          </p>
        </motion.div>

        {/* Trust Signals Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-16"
        >
          {trustSignals.map((signal, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center text-center p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100"
            >
              <ProIcon icon={signal.icon} family="solid" className="text-xl text-brand-ocean mb-2" />
              <span className="text-xl sm:text-2xl font-black text-brand-ocean">{signal.value}</span>
              <span className="text-xs sm:text-sm font-medium text-slate-500">{signal.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Services Grid - Modern Cards with Numbered Steps */}
        <section
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-20"
          aria-label="Our Guest Services"
        >
          {services.map((service, index) => (
            <motion.article
              key={service.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group relative bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl sm:rounded-3xl p-5 sm:p-6 hover:shadow-xl hover:shadow-slate-200/50 hover:border-brand-ocean/20 transition-all duration-300"
              aria-labelledby={`service-title-${service.id}`}
            >
              {/* Content */}
              <div className="pt-2">
                {/* Title Row with Icon */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-ocean/10 flex items-center justify-center text-brand-ocean group-hover:bg-brand-ocean group-hover:text-white transition-colors duration-300">
                    <ProIcon icon={service.icon} family="solid" className="w-5 h-5" />
                  </div>
                  <h2
                    id={`service-title-${service.id}`}
                    className="text-base sm:text-lg font-black text-slate-900 tracking-tight"
                  >
                    {service.title}
                  </h2>
                </div>
                
                {/* Description */}
                <p className="text-slate-600 leading-relaxed text-sm mb-4">
                  {service.description}
                </p>

                {/* CTA Link */}
                {service.ctaLabel && service.ctaTo && (
                  <Link
                    to={service.ctaTo}
                    onClick={() => trackContactClick('service_cta', service.ctaTrack || service.id)}
                    className="ui-btn-tertiary w-full justify-center text-sm py-2.5 gap-2 group"
                    aria-label={service.ctaLabel}
                  >
                    <span>{service.ctaLabel}</span>
                    <ProIcon
                      icon="arrow-right"
                      family="light"
                      className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                    />
                  </Link>
                )}
              </div>
            </motion.article>
          ))}
        </section>

        {/* CTA Section - Conversion Optimized */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-brand-ocean to-brand-ocean-dark rounded-3xl p-8 sm:p-12 lg:p-16 text-center text-white relative overflow-hidden"
          aria-label="Contact CTA"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
              Ready to Book Smarter?
            </h2>
            <p className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Browse the homes yourself or let us shortlist the right ones for you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* Primary CTA - Browse Rentals */}
              <Link
                to="/destinations"
                onClick={() => {
                  trackEvent('select_promotion', {
                    promotion_name: 'services_page_cta',
                    creative_name: 'browse_rentals',
                  });
                }}
                className="ui-btn-primary w-full sm:w-auto justify-center text-sm sm:text-base px-8 py-4 gap-2 group"
              >
                <span>Browse Our Rentals</span>
                <ProIcon icon="chevron-right" family="light" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>

              {/* Secondary CTA - Get in Touch */}
              <Link
                to="/contact"
                onClick={() => trackContactClick('service_cta', 'contact_link')}
                className="ui-btn-secondary text-sm sm:text-base px-8 py-4 gap-2 group"
              >
                <ProIcon icon="comments" family="solid" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                <span>Get in Touch</span>
              </Link>
            </div>

            {/* Trust Microcopy */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs sm:text-sm text-white/60">
              <span className="flex items-center gap-1.5">
                <ProIcon icon="clock" family="light" className="w-3.5 h-3.5" />
                Response within 2 hours
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-1.5">
                <ProIcon icon="comments" family="light" className="w-3.5 h-3.5" />
                Real local team
              </span>
            </div>
          </div>
        </motion.section>
      </header>

      {/* Property Owners Section - Redesigned */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden"
            >
              <div className="grid md:grid-cols-2">
                {/* Left - Visual */}
                <div className="bg-gradient-to-br from-brand-ocean to-brand-ocean-dark p-8 sm:p-12 flex flex-col justify-center text-white relative overflow-hidden">
                  {/* Decorative Circles */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
                  </div>
                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                      <ProIcon icon="key" family="solid" className="w-7 h-7" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black mb-4">Own a Property in Marbella?</h2>
                    <p className="text-white/80 leading-relaxed">
                      Join a selective group of owners who benefit from stronger positioning, better guest fit, and hands-on local oversight.
                    </p>
                  </div>
                </div>
                
                {/* Right - CTA */}
                <div className="p-8 sm:p-12 flex flex-col justify-center">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Why Partner With Us</h3>
                  <ul className="space-y-3 mb-8">
                    {[
                      'Professional photography & listing',
                      'Vetted guest screening',
                      'Local maintenance & housekeeping',
                      'Higher occupancy rates'
                    ].map((benefit, i) => (
                      <li key={i} className="flex items-center gap-3 text-slate-600 text-sm">
                        <span className="w-5 h-5 rounded-full bg-brand-ocean/10 flex items-center justify-center flex-shrink-0">
                          <ProIcon icon="check" family="solid" className="w-3 h-3 text-brand-ocean" />
                        </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/owners"
                    className="ui-btn-primary w-full justify-center text-sm sm:text-base py-4 gap-2 group"
                  >
                    <span>Partner With Us</span>
                    <ProIcon icon="arrow-right" family="light" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Structured Data for SEO - Hidden */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ServicePage",
          "name": "Guest Services - SUNMARBELLA",
          "description": "Comprehensive guest services including rental matching, pre-arrival planning, personal inspection, housekeeping, family essentials, and owner partnerships in Marbella.",
          "url": "https://sunmarbella.com/services",
          "provider": {
            "@type": "Organization",
            "name": COMPANY_INFO.brandName,
            "telephone": COMPANY_INFO.phone,
            "email": COMPANY_INFO.email,
            "areaServed": "Marbella, Spain"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Guest Services",
            "itemListElement": services.map((service, index) => ({
              "@type": "Offer",
              "position": index + 1,
              "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
              }
            }))
          }
        })}
      </script>
    </main>
  );
};
